import { ReactNode } from 'react';
import { Construction, type LucideIcon } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

interface BusinessPageShellProps {
  title: string;
  description: string;
  crumbs: { label: string; href?: string }[];
  icon: LucideIcon;
  actions?: ReactNode;
  children: ReactNode;
  stats?: { label: string; value: string; hint?: string }[];
  /** Fixture-backed pages show a "Mock" badge; pass false once wired to the API. */
  mock?: boolean;
}

/** Shared chrome for Business HQ admin pages. */
export function BusinessPageShell({
  title,
  description,
  crumbs,
  icon: Icon,
  actions,
  children,
  stats = [],
  mock = true,
}: BusinessPageShellProps) {
  useBreadcrumbs([{ label: 'Admin', href: '/admin' }, { label: 'Business', href: '/admin/business' }, ...crumbs]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <Icon className="h-7 w-7 text-slate-700" />
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {mock && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                <Construction className="h-3 w-3" />
                Mock
              </span>
            )}
          </div>
          <p className="ml-10 max-w-2xl text-sm text-gray-500">{description}</p>
        </div>
        {actions}
      </div>

      {stats.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-500">{s.label}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{s.value}</p>
              {s.hint && <p className="mt-1 text-xs text-gray-400">{s.hint}</p>}
            </div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}
